import { isBornholm, validateCpr } from '@flos/react-components';
import { getStakeHolderInfo, getCustomerMarking, getInsuranceStatus } from '../../../../store/Actions';
import { SET_STAKEHOLDER, SET_STATE_CHANGE, SET_RMO_REASON, SET_HAS_ERROR } from '../../../../store/ActionKeys';
import { CUSTOMER_STATUS, EMAIL_EVENT } from '../../../../interfaces/IEmailPayload';

export const checkLoggedInUser = async (endpoints: any, dispatch: Function, c9log: any): Promise<CUSTOMER_STATUS> => {
  function createAddressFromStakeholderData(stakeholderData: any) {
    return {
      address: stakeholderData.village
        ? `${stakeholderData.streetAddress}, ${stakeholderData.village}, ${stakeholderData.zipCity}` || ''
        : `${stakeholderData.streetAddress}, ${stakeholderData.zipCity}` || '',
      addressInfo: {
        streetName: stakeholderData.streetAddress?.split(', ')[0] || '',
        houseNumber: stakeholderData.streetAddress?.split(', ')[1] || '',
        supCityName: stakeholderData.village || '',
        postalCodeName: stakeholderData.zipCity?.split(' ')[1] || '',
        zipCode: stakeholderData.zipCity?.split(' ')[0] || ''
      },
      status: 'valid'
    };
  }

  try {
    const stakeholder = (await getStakeHolderInfo(endpoints.stakeholder, dispatch, c9log)) as any;
    if (stakeholder.data) {
      dispatch({
        type: SET_STAKEHOLDER,
        payload: {
          cpr: { value: stakeholder.data.cprNo, status: 'valid' },
          name: { value: `${stakeholder.data.firstName} ${stakeholder.data.lastName}`, status: 'valid' },
          customerId: { value: stakeholder.data.customerId, status: 'valid' },
          phone: stakeholder.data.mobilePhone
            ? { value: stakeholder.data.mobilePhone, status: 'valid' }
            : { value: '', status: 'invalid' },
          email: stakeholder.data.emailAddress
            ? { value: stakeholder.data.emailAddress, status: 'valid' }
            : { value: '', status: 'invalid' },
          address:
            stakeholder.data.streetAddress && stakeholder.data.zipCity
              ? createAddressFromStakeholderData(stakeholder.data)
              : { address: '', addressInfo: {}, status: 'invalid' }
        }
      });
      dispatch({ type: SET_STATE_CHANGE, payload: true });

      const validInsurance: boolean = await checkInsuranceStatus(endpoints, c9log, stakeholder?.data.cprNo);
      if (!validInsurance) {
        return CUSTOMER_STATUS.INSURANCE_EXIST;
      }

      const canProceed: boolean = await customerMarking(endpoints, c9log);
      if (!canProceed) {
        return CUSTOMER_STATUS.MARKED;
      }

      const age: CUSTOMER_STATUS = checkForAge(stakeholder?.data.cprNo);
      if (age !== CUSTOMER_STATUS.PASS) {
        return age;
      }

      const bornholm = isBornholm(stakeholder?.data.zipCity.substr(0, 4));
      if (bornholm) {
        return CUSTOMER_STATUS.BORNHOLM;
      }
    }
    return CUSTOMER_STATUS.PASS;
  } catch (error) {
    c9log.error(`Checking customer after login failed ${error}`, 'customerChecks');
    dispatch({ type: SET_RMO_REASON, payload: EMAIL_EVENT.GENERAL_ERROR });
    dispatch({ type: SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    return Promise.resolve(CUSTOMER_STATUS.FAILURE);
  }
};

/**
 * Age must be from 18 to 68, both included
 */
const checkForAge = (cpr: string): CUSTOMER_STATUS => {
  const v = validateCpr(cpr);
  if (!v.valid || !v.age) {
    return CUSTOMER_STATUS.FAILURE;
  }
  if (v.age < 18) {
    return CUSTOMER_STATUS.AGE_BELOW_18;
  } else if (v.age > 68) {
    return CUSTOMER_STATUS.AGE_ABOVE_68;
  }
  return CUSTOMER_STATUS.PASS;
};

/**
 * Check for DFIM
 */
export const customerMarking = async (endpoints: any, c9log: any): Promise<boolean> => {
  const status = await getCustomerMarking(endpoints, c9log);
  return status.canProceed;
};

/**
 * Check for existing injury insurance
 */
const checkInsuranceStatus = async (endpoints: any, c9log: any, cprNr: string): Promise<boolean> => {
  const status = await getInsuranceStatus(endpoints.insuranceStatus, c9log, cprNr);
  return status.canProceed;
};
